"use client";

import {
  type HTMLAttributes,
  type ReactNode,
  type UIEventHandler,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type RefObject,
  type FC,
  type Ref,
} from "react";
import {
  mergeClasses,
  hasTopOverflow,
  hasBottomOverflow,
  hasLeftOverflow,
  hasRightOverflow,
  throttle,
} from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { Timeout } from "../../types";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { useMutationObserver } from "../../hooks/useMutationObserver";
import { mergeRefs } from "../../utils/mergeRefs";
import { nextFrame } from "../../utils/nextFrame";
import { usePubSub } from "../../hooks/usePubSub";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { useWindowSize } from "../../hooks/useWindowSize";

import { containerStyle, innerStyle } from "./ScrollPane.css";

export type ScrollOverflow = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};

export type ScrollPaneProps = RecipeVariants<typeof containerStyle> &
  HTMLAttributes<HTMLDivElement> & {
    className?: string;
    contentClassName?: string;
    forceOverflowBottom?: boolean;
    emit?: boolean;
    emitTag?: string;
    mode?: "horizontal" | "vertical";
    snap?: boolean;
    renderCounter?: number;
    children: ReactNode;
    innerTabIndex?: number;
    onOverflowChange?: (overflow: ScrollOverflow) => void;
    disabled?: boolean;
    /**
     * Ref forwarded to the inner scroll element
     */
    ref?: Ref<HTMLDivElement>;
  };

const THROTTLE_TIME = 150;

/**
 * ScrollPane component is a container that can be scrolled.
 */
export const ScrollPane: FC<ScrollPaneProps> = ({
  hideScrollbar = false,
  forceOverflowBottom = false,
  mode = "vertical",
  overlapColor = "background",
  overlapSize = "default",
  snap = false,
  renderCounter = 0,
  emit = false,
  emitTag,
  innerTabIndex,
  disabled = false,
  className,
  contentClassName,
  onScroll,
  onOverflowChange,
  children,
  ref,
  ...props
}) => {
  const pubSub = usePubSub();
  const windowSize = useWindowSize();
  const { ref: mutationRef } = useMutationObserver<HTMLDivElement>(() => {
    defineOverflow();
  });
  const { ref: resizeRef } = useResizeObserver(() => {
    defineOverflow();
  });

  const internalRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<Timeout | null>(null);

  // Overflow state
  // --------------

  const [overflow, setOverflow] = useState<ScrollOverflow>({
    top: false,
    bottom: false,
    left: false,
    right: false,
  });

  const defineOverflow = useCallback(
    (fromTimeout = false) => {
      if (overlapSize === "none") {
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (internalRef.current) {
        const newOverflow = {
          top: hasTopOverflow(internalRef.current),
          bottom: hasBottomOverflow(internalRef.current),
          left: hasLeftOverflow(internalRef.current),
          right: hasRightOverflow(internalRef.current),
        };

        // Only update state if values have changed
        if (JSON.stringify(newOverflow) !== JSON.stringify(overflow)) {
          setOverflow(newOverflow);
        }
      }

      if (!fromTimeout) {
        timeoutRef.current = setTimeout(() => {
          defineOverflow(true);
        }, THROTTLE_TIME);
      }
    },
    [overlapSize, overflow],
  );

  // Throttle the overflow check to avoid excessive updates
  const throttledDefineOverflow = useMemo(
    () => throttle(defineOverflow, THROTTLE_TIME),
    [defineOverflow],
  );

  useIsomorphicLayoutEffect(() => {
    throttledDefineOverflow();
  }, [children, windowSize, throttledDefineOverflow]);

  // Handle scroll
  // --------------

  const handleScroll: UIEventHandler<HTMLDivElement> = (e) => {
    if (overlapSize !== "none") {
      throttledDefineOverflow();
    }

    if (emit) {
      pubSub.publish("onScroll", {
        scrollTop: e.currentTarget.scrollTop,
        tag: emitTag,
      });
    }

    onScroll?.(e);
  };

  // Pub sub scrolling
  // ------------------

  useEffect(() => {
    if (!emitTag) {
      return;
    }

    const unsubscribeScrollToBottom = pubSub.subscribe(
      "scrollToBottom",
      ({ tag, instant }) => {
        if (internalRef.current && tag === emitTag) {
          internalRef.current.scrollTo({
            top: internalRef.current.scrollHeight,
            behavior: instant ? "instant" : "smooth",
          });
        }
      },
    );

    const unsubscribeScrollToTop = pubSub.subscribe(
      "scrollToTop",
      ({ tag }) => {
        // console.log("[ScrollPane] scrollToTop", tag, emitTag, ref.current);

        if (tag === emitTag) {
          nextFrame(() => {
            if (internalRef.current) {
              internalRef.current.scrollTo({ top: 0, behavior: "smooth" });
            }
          });
        }
      },
    );

    return () => {
      unsubscribeScrollToBottom();
      unsubscribeScrollToTop();
    };
  }, [emitTag]);

  const onOverflowChangeRef = useRef(onOverflowChange);
  useEffect(() => {
    onOverflowChangeRef.current = onOverflowChange;
  }, [onOverflowChange]);

  useEffect(() => {
    onOverflowChangeRef.current?.(overflow);
  }, [overflow]);

  return (
    <div
      ref={mergeRefs<HTMLDivElement>(
        mutationRef,
        resizeRef as RefObject<HTMLDivElement | null>,
      )}
      className={mergeClasses(
        containerStyle({
          overflowTop: mode === "vertical" ? overflow.top : overflow.left,
          overflowBottom:
            mode === "vertical"
              ? overflow.bottom || forceOverflowBottom
              : overflow.right,
          overlapColor,
          overlapSize,
          hideScrollbar,
          disabled,
        }),
        className,
      )}
      data-mode={mode}
      data-render={renderCounter}
      {...props}
    >
      <div
        ref={mergeRefs(internalRef, ref)}
        className={mergeClasses(
          innerStyle({
            hideScrollbar,
            overlapColor,
            mode,
            snapMode: snap ? mode : undefined,
          }),
          contentClassName,
        )}
        onScroll={handleScroll}
        tabIndex={innerTabIndex}
      >
        {children}
      </div>
    </div>
  );
};
